import { h } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { useDispatch, useSelector } from 'react-redux';

import { Translate } from '@travel/translation';
import { useDeviceTypes } from '@travel/traveler-core/hooks';
import {
  findFeaturesByName,
  generateDataLayerStringData,
  getDefaultURLSearchRequest,
  getSearchRatParams,
  URL_KEYS,
} from '@travel/traveler-core/utils/jrdpHelper';
import LoadingCircle from '@travel/ui/components/FlatButton/loadingCircle';
import { cx, isNotEmptyArray } from '@travel/utils';

import Breadcrumb from 'components/BreadCrumb';
import DataLayer from 'components/DataLayer';
import EmptyState from 'components/EmptyState';
import GoToTopButton from 'components/GoToTopButton';
import PaginationButton from 'components/PaginationButton';
import PriceSwitchHeader from 'components/PriceSwitchHeader';
import ProviderHeaderNavigation from 'components/ProviderHeaderNavigation';
import ProviderInfoHeader from 'components/ProviderInfoHeader';
import isLazyLoadContext from 'context/isLazyLoadContext';
import useSearchForm from 'hooks/useSearchForm';
import {
  fetchMorePlanItems,
  fetchPlanDetailsFilter,
  updatePendingFilters,
} from 'store/planDetails/actions';
import {
  getIsFetching,
  getIsFetchingFilters,
  getIsFetchingMoreItems,
  getPlanDetailsData,
  getPrefetchedFilters,
  hasMorePlanItems,
} from 'store/planDetails/selectors';
import { getProviderDetails } from 'store/providerDetails/selectors';
import PlanSearchForm from '../../components/PlanSearchForm';
import PlanInfo from './components/PlanInfo';
import PlanInfoSkeleton from './components/PlanInfo/planInfoSkeleton';
import RoomInfo from './components/RoomInfo';
import RoomInfoSkeleton from './components/RoomInfo/roomInfoSkeleton';

import { pushLocation } from 'store/__router/actions';

import { FetchPriority } from 'constants/common';
import paths from 'constants/paths';
import { PLAN_DETAIL_SORT } from 'constants/sort';
import { GSP_JRDP_MAPPING } from '@travel/traveler-core/constants/JRDPAnalytics';

import {
  getDefaultURLCustomListRequest,
  getPlaceLayers,
  getPlanDetailValidSortValue,
  toSearchQueryString,
} from 'utils/search';

import styles from './planDetail.module.scss';

import type { URLSearchQuery } from 'Common-Types';
import type { PlanDetails, RequestBody, SortCategory } from 'PlanDetails-Types';

export type Props = {
  path: string;
  /** String or provider id in the url */
  params: { providerId: string; planId: string; itemId?: string };
  /** Object of search query */
  query?: URLSearchQuery;
};

const PlanDetail = (props: Props) => {
  const { query, params } = props;

  const dispatch = useDispatch();
  const provider = useSelector(getProviderDetails);
  const isFetching = useSelector(getIsFetching);
  const prefetchedFilters = useSelector(getPrefetchedFilters);
  const isFetchingFilters = useSelector(getIsFetchingFilters);
  const planDetails = useSelector(getPlanDetailsData);
  const hasShowMore = useSelector(hasMorePlanItems);
  const isFetchingMoreItems = useSelector(getIsFetchingMoreItems);

  const {
    media,
    description,
    checkInTime,
    lastCheckInTime,
    hasMeal,
    name: planName,
    items,
    planId,
    departureStation,
    destinationStation,
    accommodationArea,
  } = planDetails;

  const { breadcrumb } = provider;

  const placeLayers = getPlaceLayers(breadcrumb, query);

  const itemIdParam = params.itemId?.replace('/', '');
  const planListUrl = accommodationArea?.pathId
    ? paths.plansList.pathResolver(accommodationArea.pathId)
    : '';
  const hasSpecificRoom = !!itemIdParam;
  const { isPC } = useDeviceTypes();
  const form = useSearchForm(query, { shouldFetchByDepartureArea: false });
  const isFormInitialized = useRef(false);

  const pagePath = paths.planDetail.pathResolver(params.providerId, params.planId, params.itemId);
  const accommodationAreaPath = planDetails.accommodationArea?.pathId;
  const accommodationsPath = planDetails.accommodations
    ?.map(accommodation => accommodation.pathId)
    .join(',');
  const specialCategories = findFeaturesByName(
    'SPECIAL_PROVIDER_CATEGORY',
    provider?.providerFeatures,
  );

  const { onDepartureChanged, onSetAccommodationArea } = form.travel;
  useEffect(() => {
    if (!isFormInitialized.current && !isFetching && planDetails.departureArea?.code) {
      if (planDetails.departureArea) onDepartureChanged(planDetails.departureArea);
      if (planDetails.accommodationArea) onSetAccommodationArea(planDetails.accommodationArea);

      isFormInitialized.current = true;
    }
  }, [planDetails, onDepartureChanged, onSetAccommodationArea, isFetching]);

  const onSortChange = useCallback(
    (sort: SortCategory) => {
      dispatch(
        pushLocation({
          pathname: pagePath,
          search: toSearchQueryString({
            ...query,
            [URL_KEYS.SORT]: sort,
          }),
        }),
      );
    },
    [dispatch, pagePath, query],
  );

  const onFilterSubmit = useCallback(
    (featureIds: Array<string>, minPrice?: number, maxPrice?: number) => {
      const updatedQueryString = toSearchQueryString({
        ...query,
        ...getDefaultURLCustomListRequest<SortCategory>({
          ...query,
          [URL_KEYS.MIN_PRICE]: minPrice,
          [URL_KEYS.MAX_PRICE]: maxPrice,
          [URL_KEYS.FEATURES]: featureIds,
        }),
      });

      dispatch(
        pushLocation({
          pathname: pagePath,
          search: updatedQueryString,
        }),
      );
    },
    [dispatch, pagePath, query],
  );

  const onFilterRefetch = useCallback(
    (featureIds: Array<string>, minPrice?: number, maxPrice?: number) => {
      const searchQuery = {
        ...getDefaultURLSearchRequest({
          ...query,
          [URL_KEYS.ACCOMMODATION_AREA]: accommodationAreaPath,
          [URL_KEYS.PLACE_OF_STAY]: accommodationsPath,
        }),
        ...getDefaultURLCustomListRequest<SortCategory>({
          ...query,
          [URL_KEYS.FEATURES]: featureIds,
          [URL_KEYS.MIN_PRICE]: minPrice,
          [URL_KEYS.MAX_PRICE]: maxPrice,
        }),
      };

      dispatch(fetchPlanDetailsFilter({ ...searchQuery, ...params, id: params.providerId }));
    },
    [dispatch, query, params, accommodationsPath, accommodationAreaPath],
  );

  const onResetFiltersPendingState = useCallback(() => {
    dispatch(updatePendingFilters(planDetails));
  }, [dispatch, planDetails]);

  const handlePagination = useCallback(() => {
    const searchQuery: RequestBody = {
      id: params.providerId,
      planId: params.planId,
      itemId: params.itemId,
      ...getDefaultURLSearchRequest({
        ...query,
        accommodationArea: accommodationAreaPath,
        placeOfStay: accommodationsPath,
      }),
      ...getDefaultURLCustomListRequest(query),
    } as RequestBody;

    dispatch(fetchMorePlanItems(searchQuery));
  }, [
    params.providerId,
    params.planId,
    params.itemId,
    query,
    accommodationAreaPath,
    accommodationsPath,
    dispatch,
  ]);

  const { id, name, providerFeatures, reviewScore, totalReviews, totalMediums } = provider;

  return (
    <div data-testid="planDetail-container" className={styles.wrapper}>
      {isPC && (
        <Breadcrumb
          placeLayers={placeLayers}
          className={cx(styles.breadcrumb, styles.bottomSpace)}
        />
      )}
      <ProviderInfoHeader
        providerId={id}
        providerName={name}
        providerFeatures={providerFeatures}
        reviewScore={reviewScore}
        totalReviews={totalReviews}
        query={query}
      />
      <ProviderHeaderNavigation
        providerId={id}
        totalReviews={totalReviews}
        totalMediums={totalMediums}
        className={styles.headerNavigation}
        query={query}
      />

      <div className={styles.container}>
        {isFetching ? (
          <PlanInfoSkeleton hotelId={provider.id} />
        ) : (
          <isLazyLoadContext.Provider
            value={{ isDisableLazyLoad: true, fetchPriority: FetchPriority.HIGH }}
          >
            <PlanInfo
              hotelId={provider.id}
              media={media}
              description={description}
              checkInTime={checkInTime}
              lastCheckInTime={lastCheckInTime}
              hasMeal={hasMeal}
              name={planName}
            />
          </isLazyLoadContext.Provider>
        )}
      </div>
      {isPC && <h3 className={styles.subText}>* {form.labels.searchConditionMessage}</h3>}
      <PlanSearchForm<PlanDetails, SortCategory>
        query={query}
        form={form}
        formAction={pagePath}
        sort={getPlanDetailValidSortValue(query?.[URL_KEYS.SORT])}
        sortOptions={PLAN_DETAIL_SORT}
        prefetchedPlans={prefetchedFilters}
        plans={planDetails}
        isFetchingPlans={isFetching}
        isFetchingFilters={isFetchingFilters}
        onFilterSubmit={onFilterSubmit}
        onFilterRefetch={onFilterRefetch}
        onSortChange={onSortChange}
        onResetPendingState={onResetFiltersPendingState}
        onDepartureChanged={form.travel.onDepartureChanged}
        hasSortAndFilter={!hasSpecificRoom}
      />
      <div>
        <PriceSwitchHeader
          hasSpecificRoom={hasSpecificRoom}
          totalCount={planDetails?.total || 0}
          isJrRakuPack={false}
        />
        <Translate className={styles.feeNote} id="DP_Common.Price.Travel_Fee.Explanation" />
      </div>
      {isFetching ? (
        <>
          <RoomInfoSkeleton isPc={isPC} />
          <div class={styles.loading}>
            <LoadingCircle classType="primary" isLoading />
          </div>
        </>
      ) : isNotEmptyArray(items) ? (
        <div>
          {items?.map(item => (
            <RoomInfo
              key={item.itemId}
              item={item}
              planId={planId}
              hotelId={provider.id}
              query={query}
              departureStation={departureStation}
              destinationStation={destinationStation}
              accommodationAreaPath={accommodationAreaPath}
              accommodationsPath={accommodationsPath}
              departureAreaCode={planDetails.departureArea?.code}
              hasMeal={hasMeal}
            />
          ))}
        </div>
      ) : (
        <div>
          <EmptyState
            title={<Translate id="Plan_List.Detail.Message.No_Result" />}
            description={<Translate id="Plan_List.Message.No_Result.Next_Action" />}
          />
        </div>
      )}
      {isFetchingMoreItems ? (
        <div class={styles.loading}>
          <LoadingCircle classType="primary" isLoading />
        </div>
      ) : (
        hasShowMore &&
        !Boolean(itemIdParam) && (
          <PaginationButton
            to={planListUrl}
            className={styles.seeMoreButton}
            limit={planDetails.limit}
            offset={planDetails.offset}
            total={planDetails.total}
            onClick={handlePagination}
          />
        )
      )}
      {!isPC && (
        <Breadcrumb
          placeLayers={placeLayers}
          className={cx(styles.breadcrumb, styles.bottomSpace)}
        />
      )}
      {!isPC && isNotEmptyArray(items) && (
        <GoToTopButton
          className={styles.goToTopButton}
          textClass={styles.goToTopButtonText}
          hasIcon={false}
        />
      )}
      {departureStation?.code && destinationStation?.code && (
        <DataLayer
          page="plan_detail"
          {...getSearchRatParams(query)}
          providerIds={id}
          providerName={name}
          itemIds={params.planId}
          providerCategories={generateDataLayerStringData(specialCategories, 'id')}
          isBookable={isNotEmptyArray(items) ? 'bookable' : 'unbookable'}
          stepParam="step2.5"
          departureStation={departureStation?.code}
          destinationStation={destinationStation?.code}
          gspRequestId={planDetails.gspRequestId}
          planDetailsGsp={GSP_JRDP_MAPPING[planDetails.searchEngine]}
        />
      )}
    </div>
  );
};

export default PlanDetail;
